<template>
  <VForm ref="form" lazy-validation class="ns-credit-card-form">
    <h2>
      <span class="title-small-bold">{{ t.formTitle }}</span>
      <span class="secure"
        ><NsIcon icon-name="icon-lock" /> <span>{{ t.secure }}</span></span
      >
    </h2>
    <VTextField
      v-model="value.cardHolderName"
      name="cardName"
      maxlength="25"
      required
      :label="t.nameLabel"
      class="flex-column card-name"
      :rules="rules"
      @invalid="nameUpdated"
    ></VTextField>
    <NsCreditCard
      v-model="value.cardNumber"
      :payment-type-id="value.paymentTypeId"
      :available-payments="paymentOptions"
      :translations="t.creditCard"
      :field-disabled="editMode"
      :prevent-jcb-bin="preventJcbBin"
      @input="updateCardNumber"
      @paymentTypeId="updatePaymentTypeId"
    ></NsCreditCard>
    <NsCreditCardDate
      ref="creditCardDateForm"
      v-model="cardDate"
      :translations="t.creditCardDate"
      class="date-field"
      :field-disabled="disableDate"
      @input="updateCardDate"
    ></NsCreditCardDate>
  </VForm>
</template>

<script>
import { VForm, VTextField } from 'vuetify/lib';
import { NsIcon } from '@nuskin/ns-icon';
import NsCreditCard from './NsCreditCard.vue';
import NsCreditCardDate from './NsCreditCardDate.vue';

/**
 * Original Author: Paul Roberts
 * Original Team: Mavericks
 *
 * NsCreditCardForm is simple component that provides a name field and imports
 * NsCreditCard and NsCreditCardDate to create a base set of form elements for
 * collecting credit card information. It is expected that this "form" will be
 * placed into a real form because it could be added to a form with a billing
 * address.
 *
 * This component takes in two props: translations and availablePayments. It
 * passes the needed translations to both NsCreditCard and NsCreditCardDate as
 * well as availablePayments to NsCreditCard.
 */
export default {
  name: 'NsCreditCardForm',
  components: {
    VForm,
    VTextField,
    NsCreditCard,
    NsCreditCardDate,
    NsIcon,
  },
  props: {
    /**
     * Available Payments for the market
     */
    availablePayments: {
      type: Array,
      default() {
        return [];
      },
    },

    value: {
      type: Object,
      default() {
        return {};
      },
    },

    /**
     * Translations as a key:value object
     */
    translations: {
      type: Object,
      default() {
        return {
          nameLabel: 'Name on Card',
          cardNumLabel: 'Card Number',
          expDateLabel: 'Expiration Date',
          fieldRequired: 'Field is required',
          numbersNotAllowed: 'Numbers are not allowed',
          secure: 'Secure',
          formTitle: 'Credit/Debit Card',
          creditCard: {
            fieldLabel: 'Card Number',
            fieldRequired: 'Field Required',
            notRecognizedCard: 'Your card is not recognized',
            invalidJcbNumber:
              "We don't currently support JCB cards that begin with 357401",
            placeholder: '    -    -    -    ',
          },
          creditCardDate: {
            label: 'Expiration Date',
            monthPlaceholder: 'mm',
            yearPlaceholder: 'yyyy',
            fieldRequired: 'Required',
          },
        };
      },
    },
    /**
     * Edit Mode
     */
    editMode: {
      type: Boolean,
      default: false,
    },
    /**
     * CX16-3221 Feature to prevent certain JCB Bin numbers
     */
    preventJcbBin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rules: [],
      cardDate: {
        expMonth: this.value.expMonth,
        expYear: this.value.expYear,
      },
      disableDate: false,
    };
  },
  computed: {
    t() {
      return this.translations;
    },
    paymentOptions() {
      return this.availablePayments;
    },
  },
  watch: {
    value() {
      if (
        !this.cardDate ||
        this.cardDate.expMonth !== this.value.expMonth ||
        this.cardDate.expYear !== this.value.expYear
      ) {
        this.cardDate = {
          expMonth: this.value.expMonth,
          expYear: this.value.expYear,
        };
      }
    },
    t() {
      if (Object.keys(this.t).length > 0) {
        // This is a hack.  The only way I have found to reset the rules with new translations
        // is to clear them out and wait for a while then reset them
        this.rules = [];
        setTimeout(() => this.initNameRules(), 10);
      }
    },
  },
  mounted() {
    this.initNameRules();
  },
  methods: {
    nameUpdated(str) {
      this.$emit('input', { ...this.value, cardHolderName: str.trim() });
    },
    updateCardNumber(str) {
      this.$emit('input', { ...this.value, cardNumber: str });
    },
    updateCardDate(dateObj) {
      this.$set(this.cardDate, 'expMonth', dateObj.expMonth);
      this.$set(this.cardDate, 'expYear', dateObj.expYear);
      this.$emit('input', {
        ...this.value,
        expMonth: dateObj.expMonth,
        expYear: dateObj.expYear,
      });
    },
    updatePaymentTypeId(id) {
      this.$emit('input', { ...this.value, paymentTypeId: id });
    },
    initNameRules() {
      // No numbers or blank spaces
      this.rules = [
        (v) => {
          return (
            (!!v && !!v.trim()) || this.t.fieldRequired || 'Field is required'
          );
        },
        (v) => {
          return (
            (!!v && !/[0-9]/.test(v.trim())) ||
            this.t.numbersNotAllowed ||
            'Numbers are not allowed'
          );
        },
      ];
    },
    validate() {
      const formValid = this.$refs.form.validate();
      const ccFormValid = this.$refs.creditCardDateForm.validate();
      // must use variables so both validations always execute
      return formValid && ccFormValid;
    },
    reset() {
      this.$refs.form.reset();
      this.$refs.creditCardDateForm.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";

::v-deep *:not(.v-icon) {
  font-family: map.get(typographyVariables.$typography, font-family-primary);

  input[type='text']:focus {
    border: 1px solid map.get(colors.$theme, 'accent-color-b') !important;
  }
}

.ns-credit-card-form {
  .date-field {
    max-width: 220px;
  }

  h2 {
    display: flex;
    justify-content: space-between;
    margin-bottom: 15px;
  }

  .secure {
    display: flex;
    font-size: 14px;
    text-transform: uppercase;
    line-height: 24px;
    font-weight: map.get(typographyVariables.$typography, font-weight-semibold);

    .icon {
      height: 20px;
      margin-top: -1px;
    }

    span {
      padding-left: 5px;
    }
  }
}
</style>
